import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import { Shield, CreditCard, Clock, Check, ArrowRight, Crown, Star, X, ArrowsClockwise, Lock } from '@phosphor-icons/react';
import WhyMembersStay from '../components/billing/WhyMembersStay';
// Stage 20B+ Wave F — pilot telemetry.
import { track, ACTIVATION_EVENTS } from '../services/activation';

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const BillingPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pollingSession, setPollingSession] = useState(null);

  const loadBilling = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/status`);
      setBilling(data);
    } catch {
      toast.error('Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBilling();
    // Stage 20B+ Wave F — fire `billing_viewed` once on mount per pilot funnel.
    track(ACTIVATION_EVENTS.BILLING_VIEWED, {});
  }, [loadBilling]);

  // Poll for checkout status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    setPollingSession(sessionId);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { setPollingSession(null); return; }
      attempts++;
      try {
        const { data } = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          toast.success('Payment successful! Your subscription is now active.');
          setPollingSession(null);
          loadBilling();
          window.history.replaceState({}, '', '/billing');
          return;
        }
        if (data.status === 'expired') {
          toast.error('Payment session expired.');
          setPollingSession(null);
          window.history.replaceState({}, '', '/billing');
          return;
        }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, loadBilling]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    // Stage 20B+ Wave F — paid-intent funnel signal. Captures the moment
    // the user *commits* to enter Stripe, regardless of whether they
    // complete payment or bounce back from the hosted checkout.
    track(ACTIVATION_EVENTS.CHECKOUT_STARTED, {});
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? You can reactivate anytime.')) return;
    try {
      await axios.post(`${API}/billing/cancel`);
      toast.success('Subscription canceled. Your data is preserved.');
      loadBilling();
    } catch {
      toast.error('Failed to cancel');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#1a3a30] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const b = billing || {};
  const isFoundational = b.is_founder_member || b.foundational_granted;
  const isPaid = b.plan_type === 'active_paid';
  const isTrial = b.plan_type === 'trial_active';
  const isExpired = b.plan_type === 'trial_expired';
  const isCanceled = b.plan_type === 'canceled';

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="billing-page">
      <div className="max-w-lg mx-auto px-4 py-8 pb-24">
        <h1 className="text-xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Billing & Subscription</h1>

        {pollingSession && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-center gap-3" data-testid="payment-polling">
            <ArrowsClockwise size={18} className="text-amber-600 animate-spin" />
            <p className="text-sm text-amber-700">Verifying payment...</p>
          </div>
        )}

        {/* Plan Status Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="plan-status-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {isFoundational && <Crown size={18} className="text-amber-500" weight="fill" />}
                {isPaid && <Star size={18} className="text-emerald-500" weight="fill" />}
                <h2 className="text-base font-bold text-[#0F172A]">
                  {isFoundational ? 'Founder Member ✦' : isPaid ? 'Kindra Subscription' : isTrial ? 'Free Trial' : isExpired ? 'Trial Ended' : isCanceled ? 'Canceled' : 'Kindra'}
                </h2>
              </div>
              <p className="text-xs text-slate-500">
                {isFoundational ? 'Free forever — you are one of our founding members.' :
                 isPaid ? `A$${b.subscription_price} AUD / month` :
                 isTrial ? `${b.days_left || 0} days remaining` :
                 isExpired ? 'Your trial has ended. Subscribe to unlock full access.' :
                 isCanceled ? 'Your subscription was canceled. Data is preserved.' :
                 'Kindra Health'}
              </p>
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
              isFoundational ? 'bg-amber-50 text-amber-600' :
              isPaid ? 'bg-emerald-50 text-emerald-600' :
              isTrial ? 'bg-blue-50 text-blue-600' :
              'bg-slate-100 text-slate-500'
            }`} data-testid="plan-badge">
              {b.plan_type?.replace('_', ' ') || 'trial'}
            </span>
          </div>

          {/* Trial Progress */}
          {isTrial && b.days_left !== undefined && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>{b.days_left} days left</span>
                <span>30 days</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#1a3a30] rounded-full transition-all" style={{ width: `${Math.max(2, ((30 - (b.days_left || 0)) / 30) * 100)}%` }} />
              </div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 text-xs">
            {b.trial_end && isTrial && (
              <div className="flex justify-between text-slate-500"><span>Trial ends</span><span className="font-medium text-slate-700">{fmtDate(b.trial_end)}</span></div>
            )}
            {isPaid && b.next_billing_date && (
              <div className="flex justify-between text-slate-500"><span>Next billing</span><span className="font-medium text-slate-700">{fmtDate(b.next_billing_date)}</span></div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isFoundational && (
          <div className="space-y-3">
            {(isTrial || isExpired || isCanceled) && (
              <>
                {/* Stage 20B+ Wave A — Stripe payment reassurance strip.
                    Sits directly above the CTA to reduce hesitation at the
                    exact moment the user is about to commit. Truthful,
                    specific, premium. */}
                <div
                  className="rounded-xl border border-[rgba(14,28,46,0.10)] bg-[#FBFAF6] p-4 mb-1"
                  data-testid="billing-stripe-reassurance"
                >
                  <div className="flex items-start gap-2.5">
                    <Lock size={14} weight="duotone" className="text-[#2FB3A0] mt-[2px] flex-shrink-0" />
                    <div>
                      <p className="text-[12px] text-[#0E1C2E] leading-[1.5] font-medium">
                        Payment handled securely by Stripe.
                      </p>
                      <p className="text-[11.5px] text-slate-500 leading-[1.55] mt-1">
                        Your card details never touch our servers. Cancel anytime from this page — no email, no call, no friction.
                      </p>
                    </div>
                  </div>
                </div>
                <button onClick={handleCheckout} disabled={checkoutLoading}
                  className="w-full bg-[#1a3a30] text-white rounded-xl py-3.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#163029] disabled:opacity-50 transition-colors"
                  data-testid="subscribe-btn">
                  <CreditCard size={16} />
                  {checkoutLoading ? 'Loading...' : `${isExpired || isCanceled ? 'Subscribe' : 'Subscribe Now'} — A$${b.subscription_price || '9.99'} / month`}
                </button>
              </>
            )}
            {isPaid && (
              <button onClick={handleCancel}
                className="w-full text-red-500 text-sm py-3 hover:bg-red-50 rounded-xl transition-colors"
                data-testid="cancel-btn">
                Cancel Subscription
              </button>
            )}
          </div>
        )}

        {/* What's Included */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="features-card">
          <h3 className="text-sm font-bold text-[#0F172A] mb-3">{isPaid || isFoundational ? 'Your plan includes' : 'Full access includes'}</h3>
          <div className="space-y-2.5">
            {[
              'Unlimited AI health companion chat',
              'Health condition & medication tracking',
              'Appointment preparation tools',
              'Document storage & analysis',
              'Health insights & pattern detection',
              'Personalised guidance over time',
            ].map(f => (
              <div key={f} className="flex items-center gap-2.5">
                <Check size={14} className={`flex-shrink-0 ${isPaid || isFoundational || isTrial ? 'text-emerald-500' : 'text-slate-300'}`} weight="bold" />
                <span className={`text-xs ${isPaid || isFoundational || isTrial ? 'text-slate-600' : 'text-slate-400'}`}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stage 20B+ Wave B — Sample brief proof asset. Shown to
            non-foundational users (trial, expired, canceled, paid) so they can
            see the actual output Kindra produces before committing — the
            highest-leverage conversion move for a companion product. */}
        {!isFoundational && (
          <a
            href="/sample-brief"
            target="_blank"
            rel="noreferrer"
            className="block mt-4 rounded-xl border border-[rgba(14,28,46,0.10)] bg-[#FBFAF6] p-4 hover:bg-white transition-colors"
            data-testid="billing-sample-brief-link"
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(14,28,46,0.08)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2FB3A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="13" y2="17" />
                </svg>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-[#0E1C2E] leading-[1.4]">
                  See what Kindra produces
                </p>
                <p className="text-[12px] text-slate-500 leading-[1.55] mt-1">
                  A real example of an appointment brief — the kind you get when you walk into your next visit already understood.
                </p>
              </div>
              <span className="text-slate-400 text-[13px] mt-[2px]" aria-hidden>→</span>
            </div>
          </a>
        )}

        {/* Stage 15 P3 — conversion confidence. Shown to trial / expired /
            canceled users (NOT founders or active paid — they don't need
            convincing). Calm, factual, no hype. */}
        {!isFoundational && !isPaid && (
          <WhyMembersStay className="mt-6" />
        )}

        {/* Read-only Notice */}
        {(isExpired || isCanceled) && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4" data-testid="readonly-notice">
            <p className="text-xs text-slate-600 leading-relaxed">
              <strong>Your data is safe.</strong> You can still log in and view your health information, insights, and history.
              Subscribe to unlock chat, add new entries, and get personalised guidance.
            </p>
          </div>
        )}

        {/* Trust Footer */}
        <div className="flex items-center justify-center gap-2 mt-8 text-[10px] text-slate-400">
          <Shield size={12} />
          <span>Secure payment via Stripe. Cancel anytime.</span>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
